import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import Modal from "../../../shared/ui/Modal";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import { useTranslation } from "../../../i18n";
import { accountStatusLabel } from "../../../lib/accountStatus";
import { formatAdminError } from "../adminApi";
import {
  createAdminOffer,
  fetchAdminOfferCustomers,
  updateAdminOffer,
} from "../adminOffersApi";

const FIELD =
  "mt-1 w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2.5 text-body-md outline-none transition focus:ring-2 focus:ring-secondary";
const LABEL = "block text-label-sm font-semibold text-on-surface-variant";

function emptyForm(customer) {
  return {
    userId: customer?.id ?? null,
    customer: customer ?? null,
    title: "",
    description: "",
    price: "",
    billingInterval: "monthly",
    features: [{ label: "", included: true }],
    limits: [{ label: "", value: "" }],
    validUntil: "",
    adminNotes: "",
    activate: true,
  };
}

function formFromOffer(offer) {
  return {
    userId: offer.customer?.id ?? null,
    customer: offer.customer ?? null,
    title: offer.title,
    description: offer.description,
    price: String(offer.price),
    billingInterval: offer.billingInterval,
    features: offer.features.length ? offer.features.map((f) => ({ ...f })) : [{ label: "", included: true }],
    limits: offer.limits.length ? offer.limits.map((l) => ({ ...l })) : [{ label: "", value: "" }],
    validUntil: offer.validUntil ? toLocalInput(offer.validUntil) : "",
    adminNotes: offer.adminNotes || "",
    activate: false,
  };
}

function toLocalInput(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/**
 * Customer search + pick (only client account owners are returned).
 * Results float in a dropdown that opens on focus/typing and closes on
 * outside click, Escape or selection — it never pushes the form down.
 */
function CustomerPicker({ value, onChange }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = React.useRef(null);
  const listId = useId();

  useEffect(() => {
    if (value || !open) return undefined;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const data = await fetchAdminOfferCustomers({ search: query.trim() || undefined });
        setResults(data.results.slice(0, 8));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, query ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [query, value, open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (value) {
    return (
      <div className="mt-1 flex items-center justify-between gap-3 rounded-xl border border-secondary/30 bg-secondary/5 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-primary">{value.fullName || value.email}</p>
          <p className="truncate text-[12.5px] text-on-surface-variant">
            {value.email}
            {value.companyName ? ` · ${value.companyName}` : ""}
            {value.accountStatus ? ` · ${accountStatusLabel(value.accountStatus, t)}` : ""}
          </p>
        </div>
        {onChange ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="shrink-0 text-[12.5px] font-semibold text-secondary hover:underline"
          >
            {t("adminOffers.editor.change")}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative mt-1" ref={rootRef}>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant">
          <SearchIcon />
        </span>
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("adminOffers.editor.customerSearch")}
          className={`${FIELD} !mt-0 pl-10`}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          autoFocus
        />
      </div>
      {open ? (
      <ul
        id={listId}
        role="listbox"
        className="absolute left-0 right-0 top-full z-30 mt-2 max-h-64 divide-y divide-outline-variant/20 overflow-y-auto rounded-xl border border-outline-variant/30 bg-surface-container-lowest shadow-[0_12px_40px_rgba(15,23,42,0.16)]"
      >
        {searching && results.length === 0 ? (
          <li className="px-4 py-3 text-[13px] text-on-surface-variant">…</li>
        ) : results.length === 0 ? (
          <li className="px-4 py-3 text-[13px] text-on-surface-variant">{t("adminOffers.noCustomers")}</li>
        ) : (
          results.map((c) => (
            <li key={c.id} role="option" aria-selected={false}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition hover:bg-surface-container-low"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-primary">{c.fullName || c.email}</span>
                  <span className="block truncate text-[12px] text-on-surface-variant">
                    {c.email}
                    {c.companyName ? ` · ${c.companyName}` : ""}
                  </span>
                </span>
                {c.currentOffer ? (
                  <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold uppercase text-amber-800 ring-1 ring-amber-200">
                    {t(`customOffer.status.${c.currentOffer.status}`)}
                  </span>
                ) : null}
              </button>
            </li>
          ))
        )}
      </ul>
      ) : null}
      <p className="mt-1.5 text-[12px] text-on-surface-variant">{t("adminOffers.editor.customerHint")}</p>
    </div>
  );
}

/**
 * Create / edit a custom offer.
 * @param {{open, onClose, onSaved, offer?: object|null, customer?: object|null}} props
 */
const AdminOfferEditorModal = ({ open, onClose, onSaved, offer = null, customer = null }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const editing = Boolean(offer);
  const [form, setForm] = useState(() => (offer ? formFromOffer(offer) : emptyForm(customer)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(offer ? formFromOffer(offer) : emptyForm(customer));
    setError("");
    setSaving(false);
  }, [open, offer, customer]);

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  const intervalOptions = useMemo(
    () => [
      { value: "monthly", label: t("customOffer.interval.monthly") },
      { value: "yearly", label: t("customOffer.interval.yearly") },
      { value: "one_time", label: t("customOffer.interval.one_time") },
    ],
    [t]
  );

  const updateRow = (key, index, patch) =>
    set({ [key]: form[key].map((row, i) => (i === index ? { ...row, ...patch } : row)) });
  const removeRow = (key, index) =>
    set({ [key]: form[key].length === 1 ? form[key] : form[key].filter((_, i) => i !== index) });
  const addRow = (key, row) => set({ [key]: [...form[key], row] });

  const validate = () => {
    if (!editing && !form.userId) return t("adminOffers.editor.errorCustomer");
    if (form.title.trim().length < 3) return t("adminOffers.editor.errorTitle");
    const price = Number(String(form.price).replace(",", "."));
    if (!Number.isFinite(price) || price < 0) return t("adminOffers.editor.errorPrice");
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = editing ? await updateAdminOffer(offer.id, form) : await createAdminOffer(form);
      onSaved?.(saved);
    } catch (err) {
      setError(formatAdminError(err));
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !saving && onClose?.()} labelledBy={titleId}>
      <form onSubmit={handleSubmit} className="flex max-h-[90vh] flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-outline-variant/30 px-8 pb-4 pt-7 md:px-10">
          <div>
            <p className="font-label-caps text-label-caps uppercase tracking-widest text-secondary">
              {t("adminOffers.eyebrow")}
            </p>
            <h2 id={titleId} className="mt-1 font-headline-lg text-headline-lg text-primary">
              {editing
                ? t("adminOffers.editor.editTitle", { reference: offer.reference })
                : t("adminOffers.editor.createTitle")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-2 text-on-surface-variant transition hover:bg-surface-container-low hover:text-primary"
            aria-label={t("common.close")}
          >
            <MaterialIcon name="close" className="text-[20px]" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6 md:px-10">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
            <div className="space-y-5">
              <div>
                <span className={LABEL}>{t("adminOffers.editor.customer")}</span>
                <CustomerPicker
                  value={form.customer}
                  onChange={editing ? null : (c) => set({ customer: c, userId: c?.id ?? null })}
                />
              </div>

              <label className="block">
                <span className={LABEL}>{t("adminOffers.editor.title")}</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set({ title: e.target.value })}
                  placeholder={t("adminOffers.editor.titlePlaceholder")}
                  maxLength={120}
                  className={FIELD}
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-[1fr_170px]">
                <label className="block">
                  <span className={LABEL}>{t("adminOffers.editor.price")}</span>
                  <div className="relative">
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={form.price}
                      onChange={(e) => set({ price: e.target.value })}
                      className={`${FIELD} pr-12 text-[18px] font-bold tabular-nums text-primary`}
                      placeholder="0,00"
                    />
                    <span className="pointer-events-none absolute right-4 top-1/2 mt-0.5 -translate-y-1/2 font-bold text-on-surface-variant">
                      €
                    </span>
                  </div>
                </label>
                <div>
                  <span className={LABEL}>{t("adminOffers.editor.interval")}</span>
                  <div className="mt-1">
                    <EnterpriseFilterSelect
                      value={form.billingInterval}
                      onChange={(v) => set({ billingInterval: v })}
                      options={intervalOptions}
                      ariaLabel={t("adminOffers.editor.interval")}
                      usePortal
                    />
                  </div>
                </div>
              </div>

              <label className="block">
                <span className={LABEL}>{t("adminOffers.editor.description")}</span>
                <textarea
                  value={form.description}
                  onChange={(e) => set({ description: e.target.value })}
                  rows={4}
                  maxLength={4000}
                  placeholder={t("adminOffers.editor.descriptionPlaceholder")}
                  className={`${FIELD} resize-y`}
                />
              </label>

              <label className="block">
                <span className={LABEL}>{t("adminOffers.editor.validUntil")}</span>
                <input
                  type="datetime-local"
                  value={form.validUntil}
                  onChange={(e) => set({ validUntil: e.target.value })}
                  className={FIELD}
                />
              </label>
            </div>

            <div className="space-y-5">
              <div>
                <span className={LABEL}>{t("adminOffers.editor.features")}</span>
                <ul className="mt-1 space-y-2">
                  {form.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateRow("features", i, { included: !f.included })}
                        title={t("adminOffers.editor.included")}
                        className={[
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition",
                          f.included
                            ? "border-secondary/40 bg-secondary/10 text-secondary"
                            : "border-outline-variant bg-surface-container-low text-outline",
                        ].join(" ")}
                      >
                        <MaterialIcon name={f.included ? "check_circle" : "cancel"} filled={f.included} className="text-[20px]" />
                      </button>
                      <input
                        type="text"
                        value={f.label}
                        onChange={(e) => updateRow("features", i, { label: e.target.value })}
                        placeholder={t("adminOffers.editor.featurePlaceholder")}
                        maxLength={160}
                        className={`${FIELD} !mt-0`}
                      />
                      <button
                        type="button"
                        onClick={() => removeRow("features", i)}
                        className="shrink-0 rounded-lg p-2 text-outline transition hover:bg-error-container hover:text-on-error-container"
                        aria-label={t("adminOffers.delete")}
                      >
                        <MaterialIcon name="delete" className="text-[18px]" />
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => addRow("features", { label: "", included: true })}
                  className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-secondary hover:underline"
                >
                  <MaterialIcon name="add" className="text-[16px]" />
                  {t("adminOffers.editor.addFeature")}
                </button>
              </div>

              <div>
                <span className={LABEL}>{t("adminOffers.editor.limits")}</span>
                <ul className="mt-1 space-y-2">
                  {form.limits.map((l, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={l.label}
                        onChange={(e) => updateRow("limits", i, { label: e.target.value })}
                        placeholder={t("adminOffers.editor.limitLabel")}
                        maxLength={80}
                        className={`${FIELD} !mt-0`}
                      />
                      <input
                        type="text"
                        value={l.value}
                        onChange={(e) => updateRow("limits", i, { value: e.target.value })}
                        placeholder={t("adminOffers.editor.limitValue")}
                        maxLength={80}
                        className={`${FIELD} !mt-0 w-36 shrink-0 font-semibold text-primary`}
                      />
                      <button
                        type="button"
                        onClick={() => removeRow("limits", i)}
                        className="shrink-0 rounded-lg p-2 text-outline transition hover:bg-error-container hover:text-on-error-container"
                        aria-label={t("adminOffers.delete")}
                      >
                        <MaterialIcon name="delete" className="text-[18px]" />
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => addRow("limits", { label: "", value: "" })}
                  className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-secondary hover:underline"
                >
                  <MaterialIcon name="add" className="text-[16px]" />
                  {t("adminOffers.editor.addLimit")}
                </button>
              </div>

              <label className="block">
                <span className={LABEL}>{t("adminOffers.editor.notes")}</span>
                <textarea
                  value={form.adminNotes}
                  onChange={(e) => set({ adminNotes: e.target.value })}
                  rows={3}
                  maxLength={4000}
                  className={`${FIELD} resize-y`}
                />
              </label>

              {!editing ? (
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-secondary/30 bg-secondary/5 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={form.activate}
                    onChange={(e) => set({ activate: e.target.checked })}
                    className="mt-1 h-4 w-4 accent-[#006a61]"
                  />
                  <span>
                    <span className="block text-[14px] font-semibold text-primary">
                      {t("adminOffers.editor.activateNow")}
                    </span>
                    <span className="block text-[12.5px] leading-relaxed text-on-surface-variant">
                      {t("adminOffers.editor.activateHint")}
                    </span>
                  </span>
                </label>
              ) : null}
            </div>
          </div>
        </div>

        <div className="border-t border-outline-variant/30 bg-surface-container-low/50 px-8 py-4 md:px-10">
          {error ? (
            <p className="mb-3 rounded-lg border border-error/30 bg-error-container px-4 py-2.5 text-[13px] text-on-error-container">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-full px-4 py-2.5 text-[14px] font-medium text-on-surface-variant transition hover:text-primary disabled:opacity-50"
            >
              {t("adminOffers.editor.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-secondary px-7 py-2.5 text-[14px] font-bold text-white shadow-[0_6px_20px_rgba(0,106,97,0.3)] transition hover:opacity-90 disabled:opacity-60"
            >
              {saving
                ? t("adminOffers.editor.saving")
                : editing
                  ? t("adminOffers.editor.save")
                  : form.activate
                    ? t("adminOffers.editor.createAndSend")
                    : t("adminOffers.editor.create")}
              <MaterialIcon name={editing ? "save" : form.activate ? "send" : "add"} className="text-[18px]" />
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AdminOfferEditorModal;
